import React, { useEffect, useState } from "react"
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material"
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat"
import ModeEditIcon from "@mui/icons-material/ModeEdit"
import SaveIcon from "@mui/icons-material/Save"
import { useAccessContext } from "@/contexts/AccessContext"
import { coreGet, corePatch } from "@/services/coreApi"
import { useRouteStore } from "@/features/route/state/RouteStore.jsx"
import { normalizeTreeMetadata } from "@/features/route/state/RouteAdapters.jsx"
import {
  SHIP_TYPE_OPTIONS,
  validateDecimal,
} from "@/features/voyagePlanner/voyageSettingsFields"
import { CII_RATINGS } from "@/features/voyagePlanner/fuelRequestParams"
import { DEFAULT_WEATHER_LIMITS } from "@/config/defaults"
import { LIMIT_DEFAULT_FIELDS } from "@/features/vessel/vesselMeta.js"
import { SECTION_LABEL_SX } from "@/features/voyagePlanner/wizard/styles"
import {
  PANEL_BUTTON_SX,
  PANEL_BUTTON_OUTLINED_SX,
  PANEL_SCROLLBAR_SX,
} from "@/theme/panelStyles"

// Inline row editor Select (55% column), shared by the vessel editors and CII.
const INLINE_SELECT_SX = {
  width: "55%",
  borderRadius: "6px",
  fontSize: "12px",
  fontWeight: 600,
  color: "#004f7d",
  bgcolor: "#fff",
  "& .MuiSelect-select": {
    py: 0.4,
    px: 0.75,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "block",
  },
}

function SectionHeader({ label, badge, badgeColor }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
      <Typography sx={{ ...SECTION_LABEL_SX, mb: 0 }}>{label}</Typography>
      <Chip
        label={badge}
        size="small"
        sx={{
          height: 16,
          fontSize: "8.5px",
          fontWeight: 700,
          color: "#fff",
          bgcolor: badgeColor,
          "& .MuiChip-label": { px: 0.75 },
        }}
      />
    </Box>
  )
}

const EDIT_INPUT_SX = {
  width: "55%",
  "& .MuiOutlinedInput-root": { borderRadius: "6px", bgcolor: "#fff" },
  "& .MuiInputBase-input": {
    fontSize: "12px",
    fontWeight: 600,
    color: "#004f7d",
    py: 0.4,
    px: 0.75,
    textAlign: "right",
  },
  "& .MuiFormHelperText-root": { m: 0, fontSize: "9px", textAlign: "right" },
}

// Numeric vessel fields: must be positive numbers, gross tonnage may be zero.
const NUMERIC_FIELDS = {
  imo: { minValue: Number.EPSILON },
  lengthM: { minValue: Number.EPSILON },
  beamM: { minValue: Number.EPSILON },
  maxDraftM: { minValue: Number.EPSILON },
  grossTonnage: { minValue: 0 },
  deadweightTonnes: { minValue: 0 },
  mmsi: { minValue: Number.EPSILON },
}

const SPEC_ROWS = [
  ["IMO Number", "imo"],
  ["Length [m]", "lengthM"],
  ["Beam [m]", "beamM"],
  ["Max Draft [m]", "maxDraftM"],
  ["Gross Tonnage [t]", "grossTonnage"],
  ["Deadweight [t]", "deadweightTonnes"],
  ["Current CII Rating", "ciiRating"],
  ["Ship Type(s)", "shiptype"],
  ["Nationality", "nationality"],
]

const FLEET_ROWS = [
  ["MMSI", "mmsi"],
  ["Fleet Name", "fleetname"],
  ["Owner", "owner"],
  ["Charterer", "charterer"],
]

const FIELD_ORDER = [...SPEC_ROWS, ...FLEET_ROWS].map(([, field]) => field)

// Optional vessel defaults: empty = system default, so no 0 sentinel.
const WEATHER_ROWS = [
  ["Wind [kn]", "defaultWindLimit"],
  ["Wave [m]", "defaultWaveLimit"],
  ["Current [kn]", "defaultCurrentLimit"],
]
const WEATHER_MAX = { defaultWindLimit: 100, defaultWaveLimit: 20, defaultCurrentLimit: 20 }
const SYSTEM_DEFAULT = Object.fromEntries(
  Object.entries(LIMIT_DEFAULT_FIELDS).map(([limit, field]) => [field, DEFAULT_WEATHER_LIMITS[limit]]),
)
const WEATHER_FIELDS = WEATHER_ROWS.map(([, field]) => field)

let nationalityOptionsCache = null

// A cleared numeric field persists as the 0 sentinel — render it as "n/a".
// Also used by RouteDrillDown to keep partial meta seeds from hiding values.
export const isMissingSpecValue = (field, value) =>
  value === "" ||
  value == null ||
  (NUMERIC_FIELDS[field] && Number(value) === 0)

// Read-only vessel data with inline editing (Edit vessel data → fields become
// editable in place; Save persists via PATCH /vessels).
function VesselSpecTab({
  vesselMeta,
  defaultPlannerVesselSettings,
  vesselLabel,
  vesselId,
  setDraftPlannerVesselSettings,
  onVesselSaved,
  showRnwIntro = true,
  showVesselChip = true,
  // Vessel defaults belong to vessel management (Fleet Explorer), not the planner.
  showWeatherDefaults = false,
  scrollBody = false,
}) {
  const { accessContext, setAccessContext } = useAccessContext()
  const { upsertEntities } = useRouteStore()

  const [isEditing, setIsEditing] = useState(false)
  const [editValues, setEditValues] = useState({})
  const [editErrors, setEditErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(false)
  const [nationalityOptions, setNationalityOptions] = useState([])

  const meta = vesselMeta ?? {}
  const defaults = defaultPlannerVesselSettings ?? {}
  const resolvedValue = (field) => meta[field] ?? defaults[field] ?? ""

  // Leaving the vessel (or its meta refreshing) discards a stale edit session.
  useEffect(() => {
    setIsEditing(false)
    setSaveError(false)
  }, [vesselId])

  // Nationality options are only needed while editing. Module-cached: the
  // Fleet Explorer remounts this tab per Edit and must not re-download.
  useEffect(() => {
    if (!isEditing || nationalityOptions.length) return
    if (nationalityOptionsCache) {
      setNationalityOptions(nationalityOptionsCache)
      return
    }
    const load = async () => {
      try {
        const data = await coreGet(
          "/geo/mid-code-to-country",
          accessContext,
          setAccessContext,
          ".core /geo/mid-code-to-country error=",
        )
        const features = Array.isArray(data) ? data : (data?.features ?? [])
        nationalityOptionsCache = features
        setNationalityOptions(features)
      } catch (error) {
        console.error(error)
      }
    }
    load()
  }, [isEditing, nationalityOptions.length, accessContext, setAccessContext])

  const startEditing = () => {
    setEditValues(
      Object.fromEntries(
        [...FIELD_ORDER, ...(showWeatherDefaults ? WEATHER_FIELDS : [])].map(
          (field) => [field, resolvedValue(field)],
        ),
      ),
    )
    setEditErrors({})
    setSaveError(false)
    setIsEditing(true)
  }

  const cancelEditing = () => {
    setIsEditing(false)
    setEditValues({})
    setEditErrors({})
    setSaveError(false)
  }

  const handleFieldChange = (field) => (event) => {
    const raw = event.target.value
    const rules = WEATHER_MAX[field]
      ? { minValue: Number.EPSILON, maxValue: WEATHER_MAX[field], allowEmpty: true }
      : NUMERIC_FIELDS[field]
    if (rules) {
      const { value, invalid } = validateDecimal(raw, rules)
      setEditValues((prev) => ({ ...prev, [field]: value }))
      setEditErrors((prev) => ({ ...prev, [field]: invalid }))
      return
    }
    setEditValues((prev) => ({ ...prev, [field]: raw }))
  }

  const hasErrors = Object.values(editErrors).some(Boolean)

  const saveVesselData = async () => {
    if (hasErrors || saving) return
    setSaving(true)
    setSaveError(false)
    const s = editValues

    // Cleared fields persist as the DB's NOT-NULL sentinels (0 / "") so a
    // clear is a real write, not a dropped key.
    const toNumberOrZero = (value) => {
      if (value === null || value === undefined || value === "") return 0
      const raw = String(value).trim().replace(/,/g, ".")
      const num = Number(raw)
      return Number.isFinite(num) ? num : 0
    }
    const toNumberOrNull = (value) => (value === "" || value == null ? null : Number(value))

    try {
      // No concrete vessel (e.g. defaults only) → keep the edit local.
      if (Number.isFinite(Number(vesselId))) {
        const patch = {
          fleetName: s.fleetname ?? "",
          fleetOwnerName: s.owner ?? "",
          chartererName: s.charterer ?? "",
          lengthMeterRnwRestrictions: toNumberOrZero(s.lengthM),
          beamMeterRnwRestrictions: toNumberOrZero(s.beamM),
          maxdraftMeterRnwRestrictions: toNumberOrZero(s.maxDraftM),
          grossTonnageRnwRestrictions: toNumberOrZero(s.grossTonnage),
          deadweightTonnes: toNumberOrZero(s.deadweightTonnes),
          ciiRating: s.ciiRating ?? "",
          shiptypesRnwRestrictions: s.shiptype ?? "",
          nationalityRnwRestrictions: s.nationality ?? "",
          mmsiRnwRestrictions: s.mmsi ?? "",
        }
        if (showWeatherDefaults) {
          patch.defaultWindLimitKn = toNumberOrNull(s.defaultWindLimit)
          patch.defaultWaveLimitM = toNumberOrNull(s.defaultWaveLimit)
          patch.defaultCurrentLimitKn = toNumberOrNull(s.defaultCurrentLimit)
        }
        // imo is UNIQUE in the DB — clearing it to "" could collide with
        // another vessel, so it is only sent with a real value.
        if (s.imo) patch.imo = s.imo

        try {
          const data = await corePatch(
            `/vessels/${vesselId}`,
            { patch },
            accessContext,
            setAccessContext,
            ".saveVesselMeta() error=",
          )
          // The saved row goes into the RouteStore — every spec view and the
          // CII request read the vessel from there.
          if (data?.vessel) {
            upsertEntities(normalizeTreeMetadata({ vessels: [data.vessel] }))
          }
        } catch (error) {
          console.error("Error saving vessel meta:", error)
          setSaveError(true)
          return
        }
      }

      const updatedMeta = Object.fromEntries(
        FIELD_ORDER.map((field) => [field, s[field]]),
      )

      // Merge into the draft copy like the other Options tabs; the commit
      // into plannerVesselSettings happens on leaving the step.
      setDraftPlannerVesselSettings?.((prev) => ({ ...prev, ...updatedMeta }))
      onVesselSaved?.(vesselId, updatedMeta)
      setIsEditing(false)
    } finally {
      setSaving(false)
    }
  }

  const renderEditor = (field) => {
    if (field === "ciiRating") {
      return (
        <Select
          size="small"
          displayEmpty
          value={editValues.ciiRating || ""}
          onChange={(event) =>
            setEditValues((prev) => ({ ...prev, ciiRating: event.target.value }))
          }
          sx={INLINE_SELECT_SX}
        >
          <MenuItem value="" sx={{ fontSize: "12px", color: "#8a98a6" }}>
            None
          </MenuItem>
          {CII_RATINGS.map((rating) => (
            <MenuItem key={rating} value={rating} sx={{ fontSize: "12px" }}>
              {rating}
            </MenuItem>
          ))}
        </Select>
      )
    }
    if (field === "shiptype") {
      const selected = String(editValues.shiptype || "")
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean)
      return (
        <Select
          multiple
          size="small"
          value={selected}
          onChange={(event) => {
            const value = event.target.value
            const arrayValue =
              typeof value === "string" ? value.split(",") : value
            setEditValues((prev) => ({
              ...prev,
              shiptype: arrayValue.join(", "),
            }))
          }}
          sx={INLINE_SELECT_SX}
        >
          {SHIP_TYPE_OPTIONS.map((name) => (
            <MenuItem key={name} value={name} sx={{ fontSize: "12px" }}>
              {name}
            </MenuItem>
          ))}
        </Select>
      )
    }
    if (field === "nationality") {
      return (
        <Autocomplete
          disableClearable
          size="small"
          options={nationalityOptions}
          getOptionLabel={(option) => {
            const name = option?.properties?.country_common_name ?? ""
            const mid = option?.properties?.mid ?? ""
            return mid ? `${name} (${mid})` : name
          }}
          value={
            nationalityOptions.find(
              (f) =>
                f.properties.country_common_name === editValues.nationality,
            ) || null
          }
          onChange={(_e, newValue) =>
            setEditValues((prev) => ({
              ...prev,
              nationality: newValue
                ? newValue.properties.country_common_name
                : "",
            }))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              variant="outlined"
              placeholder="Select nationality"
            />
          )}
          sx={{
            width: "55%",
            "& .MuiOutlinedInput-root": {
              borderRadius: "6px",
              bgcolor: "#fff",
              py: 0,
            },
            "& .MuiInputBase-input": {
              fontSize: "12px",
              fontWeight: 600,
              color: "#004f7d",
            },
          }}
        />
      )
    }
    return (
      <TextField
        size="small"
        type="text"
        inputMode={NUMERIC_FIELDS[field] || WEATHER_MAX[field] ? "decimal" : "text"}
        value={editValues[field] ?? ""}
        onChange={handleFieldChange(field)}
        error={Boolean(editErrors[field])}
        placeholder={SYSTEM_DEFAULT[field]?.toString()}
        helperText={
          editErrors[field]
            ? WEATHER_MAX[field]
              ? `Enter a positive number up to ${WEATHER_MAX[field]}`
              : "Enter a positive number"
            : ""
        }
        slotProps={{ formHelperText: { sx: { m: 0, p: 0 } } }}
        sx={EDIT_INPUT_SX}
      />
    )
  }

  const renderRows = (rows) => (
    <Box
      sx={{
        border: "1px solid #e2e8ee",
        borderRadius: "10px",
        overflow: "hidden",
        mb: 1.25,
      }}
    >
      {rows.map(([label, field], index) => {
        const value = resolvedValue(field)
        const isMissing = isMissingSpecValue(field, value)
        return (
          <Box
            key={field}
            sx={{
              display: "flex",
              alignItems: "center",
              px: 1.5,
              py: isEditing ? 0.5 : 0.75,
              borderBottom:
                index < rows.length - 1 ? "1px solid #eef3f7" : "none",
              bgcolor: index % 2 ? "#fafcfd" : "#fff",
            }}
          >
            <Typography
              sx={{ flex: 1, fontSize: "11.5px", color: "#5b7f9c" }}
            >
              {label}
            </Typography>
            {isEditing ? (
              renderEditor(field)
            ) : (
              <Typography
                sx={{ fontSize: "12px", fontWeight: 600, color: "#004f7d" }}
              >
                {isMissing ? "n/a" : String(value)}
              </Typography>
            )}
          </Box>
        )
      })}
    </Box>
  )

  const specTables = (
    <>
      <SectionHeader
        label="Vessel Specifications"
        badge="Saved to vessel"
        badgeColor="#5b7f9c"
      />
      {renderRows(SPEC_ROWS)}

      <SectionHeader
        label="Identifiers & Fleet"
        badge="Saved to vessel"
        badgeColor="#5b7f9c"
      />
      {renderRows(FLEET_ROWS)}

      {showWeatherDefaults && (
        <>
          <SectionHeader
            label="Default Weather Limits"
            badge="Saved to vessel"
            badgeColor="#5b7f9c"
          />
          <Typography sx={{ fontSize: "10.5px", color: "#8a98a6", mb: 0.75 }}>
            Used as starting values when planning a voyage for this vessel
          </Typography>
          {renderRows(WEATHER_ROWS)}
        </>
      )}
    </>
  )

  return (
    <>
      {showRnwIntro && (
        <Typography sx={{ fontSize: "10.5px", color: "#8a98a6", my: 1.25 }}>
          Vessel & fleet data for RNW restriction checks
        </Typography>
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
        {showVesselChip ? (
          <Chip
            icon={
              <DirectionsBoatIcon
                sx={{ fontSize: 16, color: "#004f7d !important" }}
              />
            }
            label={vesselLabel || "Default vessel"}
            variant="outlined"
            sx={{
              flex: 1,
              justifyContent: "flex-start",
              height: 32,
              fontSize: "12px",
              fontWeight: 600,
              color: "#004f7d",
              borderColor: "#cfe0ec",
              backgroundColor: "#f4f8fb",
            }}
          />
        ) : (
          // Keeps the edit buttons right-aligned where the chip would sit.
          <Box sx={{ flex: 1 }} />
        )}
        {isEditing && (
          <Button
            variant="outlined"
            onClick={cancelEditing}
            disabled={saving}
            sx={PANEL_BUTTON_OUTLINED_SX}
          >
            Cancel
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={
            isEditing ? (
              <SaveIcon sx={{ fontSize: 16 }} />
            ) : (
              <ModeEditIcon sx={{ fontSize: 16 }} />
            )
          }
          onClick={isEditing ? saveVesselData : startEditing}
          disabled={saving || (isEditing && hasErrors)}
          sx={PANEL_BUTTON_SX}
        >
          {isEditing ? "Save" : "Edit"}
        </Button>
      </Box>
      {saveError && (
        <Typography sx={{ fontSize: "11px", color: "#d32f2f", mt: -0.5, mb: 1.25 }}>
          Could not save the vessel data. Please try again.
        </Typography>
      )}

      {/* scrollBody: only the tables scroll, the action row stays fixed
          (requires a flex-column parent). */}
      {scrollBody ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            ...PANEL_SCROLLBAR_SX,
          }}
        >
          {specTables}
        </Box>
      ) : (
        specTables
      )}
    </>
  )
}

export default React.memo(VesselSpecTab)
